export const PRODUCT_STOCK_UNITS = ['un', 'lb', 'oz', 'kg', 'g'] as const;

export type ProductStockUnit = (typeof PRODUCT_STOCK_UNITS)[number];

/** i18n keys in locale `products` modules; form value stays API code, menu text is human-readable */
export const PRODUCT_STOCK_UNIT_LABEL_KEYS: Record<
  ProductStockUnit,
  | 'productStockUnitOptionUn'
  | 'productStockUnitOptionLb'
  | 'productStockUnitOptionOz'
  | 'productStockUnitOptionKg'
  | 'productStockUnitOptionG'
> = {
  un: 'productStockUnitOptionUn',
  lb: 'productStockUnitOptionLb',
  oz: 'productStockUnitOptionOz',
  kg: 'productStockUnitOptionKg',
  g: 'productStockUnitOptionG',
};

export function productStockUnitMenuLabel(
  unit: ProductStockUnit,
  name: string
): string {
  return `(${unit}) ${name}`;
}

export function normalizeProductStockUnit(
  stockUnit?: string | null
): ProductStockUnit {
  if (
    stockUnit &&
    (PRODUCT_STOCK_UNITS as readonly string[]).includes(stockUnit)
  ) {
    return stockUnit as ProductStockUnit;
  }
  return 'un';
}

/** Inventory cards: `un` as integer; mass units (`lb`, `oz`, `kg`, `g`) to 2 dp */
export function formatProductStockDisplayValue(
  stock: number,
  stockUnit?: string | null
): { unit: ProductStockUnit; formatted: string; numeric: number } {
  const unit = normalizeProductStockUnit(stockUnit);
  const n = Number(stock);
  const numeric = Number.isFinite(n) ? n : 0;
  if (unit === 'un') {
    return { unit, formatted: String(Math.round(numeric)), numeric };
  }
  return { unit, formatted: numeric.toFixed(2), numeric };
}

/** e.g. `Stock (kg): 10.20` — pass translated label for "Stock" */
export function formatProductStockCardLine(
  stockLabel: string,
  stock: number,
  stockUnit?: string | null
): string {
  const { unit, formatted } = formatProductStockDisplayValue(
    stock,
    stockUnit
  );
  return `${stockLabel} (${unit}): ${formatted}`;
}
